import {
  type ClipboardEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
} from "react";
import { useDropdownContext } from "../Dropdown";
import type { SelectOption } from "../Select/types";
import { useComboBoxContext } from "./context";

export const useComboBoxTrigger = () => {
  const comboBoxContext = useComboBoxContext();
  const dropdownContext = useDropdownContext();

  const {
    filteredOptions,
    onSelect,
    searchValue,
    options: allOptions,
    multiple,
    value,
    onChange,
    setSearchValue,
  } = comboBoxContext;

  const { open, setOpen, activeIndex, setActiveIndex, contentId } =
    dropdownContext;

  const firstEnabledIndex = useCallback((opts: { disabled?: boolean }[]) => {
    return opts.findIndex((o) => !o.disabled);
  }, []);

  const activeDescId =
    activeIndex != null ? `${contentId}-option-${activeIndex}` : undefined;

  useEffect(() => {
    if (!open) return;

    if (filteredOptions.length === 0) {
      setActiveIndex(null);
      return;
    }

    if (
      activeIndex == null ||
      !filteredOptions[activeIndex] ||
      filteredOptions[activeIndex].disabled
    ) {
      const idx = firstEnabledIndex(filteredOptions);
      setActiveIndex(idx === -1 ? null : idx);
    }
  }, [open, filteredOptions, activeIndex, setActiveIndex, firstEnabledIndex]);

  const onInputKeyDownCapture = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      const key = e.key;

      if (key === "ArrowDown" || key === "ArrowUp") {
        if (!open) {
          e.preventDefault();
          setOpen(true);
          setActiveIndex(0);
          e.stopPropagation();
          return;
        }
        e.preventDefault();
        return;
      }

      if (key === "Enter" || key === " ") {
        if (!open || comboBoxContext.loading) return;
        const idx = activeIndex ?? (filteredOptions.length ? 0 : -1);
        if (idx < 0) return;

        const opt = filteredOptions[idx];
        if (opt && !opt.disabled) {
          e.preventDefault();
          e.stopPropagation();
          onSelect(opt);
        }
        return;
      }

      if (key === "Escape") {
        if (searchValue) {
          e.preventDefault();
        } else if (open) {
          e.preventDefault();
          setOpen(false);
        }
        return;
      }
    },
    [
      open,
      activeIndex,
      filteredOptions,
      onSelect,
      setOpen,
      setActiveIndex,
      searchValue,
    ],
  );

  const onInputFocus = useCallback(() => {
    if (!comboBoxContext.disabled && !open) {
      setOpen(true);
    }
  }, [comboBoxContext.disabled, open, setOpen]);

  const onInputPaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData("text").trim();
      if (!pastedText) return;

      if (!multiple) {
        // Single mode: auto-select if pasted text exactly matches an option
        const exactMatch = allOptions.find(
          (o) =>
            !o.disabled && o.text.toLowerCase() === pastedText.toLowerCase(),
        );
        if (exactMatch) {
          e.preventDefault();
          onSelect(exactMatch);
        }
      } else {
        // Multi mode: split by comma/semicolon/newline/tab and select all matches
        const pastedValues = pastedText
          .split(/[,;\n\t]+/)
          .map((s) => s.trim())
          .filter(Boolean);

        const matchingOptions = pastedValues
          .map((v) =>
            allOptions.find(
              (o) => !o.disabled && o.text.toLowerCase() === v.toLowerCase(),
            ),
          )
          .filter((o): o is SelectOption => !!o);

        if (matchingOptions.length > 0) {
          e.preventDefault();
          const currentValues = Array.isArray(value) ? value : [];
          const newValues = [...currentValues];
          for (const opt of matchingOptions) {
            if (!newValues.some((v) => v.id === opt.id)) {
              newValues.push(opt);
            }
          }
          onChange(newValues);
          setSearchValue("");
        }
      }
    },
    [allOptions, multiple, value, onSelect, onChange, setSearchValue],
  );

  const dataAttributes = Object.fromEntries(
    Object.entries(comboBoxContext).filter(([key]) => key.startsWith("data-")),
  );

  return {
    ...comboBoxContext,
    ...dropdownContext,
    activeDescId,
    onInputKeyDownCapture,
    onInputFocus,
    onInputPaste,
    dataAttributes,
    firstEnabledIndex,
  };
};
